import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Inbox,
  Lock,
  LogOut,
  Mail,
  Phone,
  RefreshCw,
  Store,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  BarChart3,
  Eye,
  MousePointerClick,
  Send,
  Apple,
  Play,
  Instagram,
  Heart,
  MessageSquare,
  Clock,
  Calendar,
  Globe,
  MapPin,
} from "lucide-react";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const T = {
  es: {
    title: "Panel de administración",
    subtitle: "Mensajes del formulario de contacto",
    password: "Contraseña",
    login: "Iniciar sesión",
    loggingIn: "Entrando...",
    wrongPassword: "Contraseña incorrecta",
    lockedOut: "Demasiados intentos fallidos. Espera 15 minutos.",
    genericError: "Algo salió mal. Intenta de nuevo.",
    logout: "Cerrar sesión",
    refresh: "Actualizar",
    messages: "mensajes",
    empty: "No hay mensajes todavía.",
    emailSent: "Email enviado",
    emailFailed: "Email no enviado",
    confirmDelete: "¿Eliminar este mensaje definitivamente?",
    loading: "Cargando...",
    backToSite: "Ver sitio web",
    loadMore: "Cargar más",
    smtpCheck: "Verificar SMTP",
    smtpChecking: "Verificando…",
    smtpOk: "SMTP OK — envío operativo",
    smtpFail: "SMTP falla",
    statsTitle: "Estadísticas",
    statsSubtitle: "Rendimiento del sitio en tiempo real",
    range1d: "24 h",
    range7d: "7 días",
    range30d: "30 días",
    rangeAll: "Todo",
    metricViews: "Visitas",
    metricAndroid: "Descargas Android",
    metricIos: "Descargas iOS",
    metricContact: "Mensajes enviados",
    metricHero: "Clics botón principal",
    metricPartner: "Clics socios",
    metricCommunity: "Clics comunidad",
    metricInstagram: "Clics Instagram",
    metricLang: "Cambios de idioma",
    metricAudienceUser: "CTA usuarios",
    metricAudiencePartner: "CTA negocios",
    dailyTitle: "Visitas por día",
    sourcesTitle: "De dónde vienen",
    sourceCol: "Fuente",
    countCol: "Cantidad",
    percentCol: "%",
    noData: "Sin datos en este período",
    tabStats: "Estadísticas",
    tabMessages: "Mensajes",
    funnelTitle: "Embudo de conversión",
    funnelSubtitle: "Del visitante al cliente / socio",
    funnelUsers: "Descarga de la app",
    funnelPartners: "Interés de socios",
    funnelStep1: "Visitas",
    funnelStep2App: "Interés en la app",
    funnelStep2Partner: "Clic para ser socio",
    funnelStep3: "Mensaje enviado",
    funnelOf: "de",
    funnelConversion: "conversión",
    hourlyTitle: "Horas más activas",
    weekdayTitle: "Días de la semana",
    localeTitle: "Idioma preferido",
    inquiriesTitle: "Tipos de consulta",
    countriesTitle: "Países",
    regionsTitle: "Provincias / regiones",
    weekdays: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
    langEs: "Español",
    langEn: "English",
  },
  en: {
    title: "Admin panel",
    subtitle: "Contact form messages",
    password: "Password",
    login: "Log in",
    loggingIn: "Logging in...",
    wrongPassword: "Wrong password",
    lockedOut: "Too many failed attempts. Wait 15 minutes.",
    genericError: "Something went wrong. Please try again.",
    logout: "Log out",
    refresh: "Refresh",
    messages: "messages",
    empty: "No messages yet.",
    emailSent: "Email sent",
    emailFailed: "Email failed",
    confirmDelete: "Permanently delete this message?",
    loading: "Loading...",
    backToSite: "View website",
    loadMore: "Load more",
    smtpCheck: "Check SMTP",
    smtpChecking: "Checking…",
    smtpOk: "SMTP OK — sending works",
    smtpFail: "SMTP failed",
    statsTitle: "Analytics",
    statsSubtitle: "Real-time site performance",
    range1d: "24 h",
    range7d: "7 days",
    range30d: "30 days",
    rangeAll: "All time",
    metricViews: "Page views",
    metricAndroid: "Android downloads",
    metricIos: "iOS downloads",
    metricContact: "Contact submits",
    metricHero: "Hero CTA clicks",
    metricPartner: "Partner clicks",
    metricCommunity: "Community clicks",
    metricInstagram: "Instagram clicks",
    metricLang: "Language switches",
    metricAudienceUser: "User CTA",
    metricAudiencePartner: "Business CTA",
    dailyTitle: "Views per day",
    sourcesTitle: "Traffic sources",
    sourceCol: "Source",
    countCol: "Count",
    percentCol: "%",
    noData: "No data in this range",
    tabStats: "Analytics",
    tabMessages: "Messages",
    funnelTitle: "Conversion funnel",
    funnelSubtitle: "From visitor to customer / partner",
    funnelUsers: "App download",
    funnelPartners: "Partner interest",
    funnelStep1: "Page views",
    funnelStep2App: "App interest",
    funnelStep2Partner: "Partner click",
    funnelStep3: "Message sent",
    funnelOf: "of",
    funnelConversion: "conversion",
    hourlyTitle: "Peak hours",
    weekdayTitle: "Weekdays",
    localeTitle: "Preferred language",
    inquiriesTitle: "Inquiry types",
    countriesTitle: "Countries",
    regionsTitle: "Provinces / regions",
    weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    langEs: "Spanish",
    langEn: "English",
  },
};

async function authedRequest(fn) {
  try {
    return await fn();
  } catch (e) {
    if (e.response?.status === 401) {
      await api.post("/auth/refresh");
      return await fn();
    }
    throw e;
  }
}

const AdminHeader = ({ t, lang, setLang, onLogout }) => (
  <header
    className="sticky top-0 z-40"
    style={{ background: "#07432a" }}
    data-testid="admin-header"
  >
    <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img src="/brand/icon-80.png" alt="Bolsita Verde" width={34} height={34} />
        <div>
          <div className="text-white font-display text-[17px] font-bold leading-tight">
            Bolsita Verde
          </div>
          <div className="text-white/60 text-[12px]">{t.title}</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="lang-toggle" role="group" aria-label="Language" data-testid="admin-lang-toggle">
          <button
            onClick={() => setLang("es")}
            className={lang === "es" ? "active" : ""}
            data-testid="admin-lang-es-btn"
          >
            ES
          </button>
          <button
            onClick={() => setLang("en")}
            className={lang === "en" ? "active" : ""}
            data-testid="admin-lang-en-btn"
          >
            EN
          </button>
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            className="flex items-center gap-2 text-[13px] font-bold text-white/85 hover:text-white px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> {t.logout}
          </button>
        )}
      </div>
    </div>
  </header>
);

const AdminLogin = ({ t, onSuccess }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { password });
      onSuccess(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) setError(t.wrongPassword);
      else if (status === 429) setError(t.lockedOut);
      else setError(t.genericError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-center px-6 py-24">
      <form onSubmit={onSubmit} className="card w-full max-w-[400px]" data-testid="admin-login-form">
        <div
          className="w-12 h-12 rounded-xl mb-5 grid place-items-center"
          style={{ background: "var(--bv-green-50)" }}
        >
          <Lock size={22} color="var(--bv-green)" />
        </div>
        <h1
          className="font-display text-[26px] mb-1"
          style={{ color: "var(--bv-heading)", fontWeight: 800 }}
        >
          {t.title}
        </h1>
        <p className="text-[14px] mb-6" style={{ color: "var(--bv-muted)" }}>
          {t.subtitle}
        </p>
        <label className="block mb-4">
          <span className="block text-[13px] font-semibold mb-1.5" style={{ color: "var(--bv-ink)" }}>
            {t.password}
          </span>
          <input
            type="password"
            required
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            data-testid="admin-password-input"
          />
        </label>
        {error && (
          <div
            className="flex items-center gap-2 text-[13px] mb-4 p-3 rounded-lg"
            style={{ background: "#fef2f2", color: "#991b1b" }}
            data-testid="admin-login-error"
          >
            <AlertCircle size={16} color="#dc2626" /> {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full justify-center"
          style={{ opacity: busy ? 0.7 : 1 }}
          data-testid="admin-login-btn"
        >
          {busy ? t.loggingIn : t.login}
        </button>
      </form>
    </div>
  );
};

const SmtpCheckButton = ({ t }) => {
  const [state, setState] = useState({ loading: false, result: null });
  const run = async () => {
    setState({ loading: true, result: null });
    try {
      const { data } = await authedRequest(() => api.get("/admin/smtp-check"));
      setState({ loading: false, result: data });
    } catch {
      setState({ loading: false, result: { ok: false, error: "network" } });
    }
  };
  const r = state.result;
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="smtp-check-wrap">
      <button
        onClick={run}
        disabled={state.loading}
        className="btn-ghost"
        style={{ padding: "10px 20px", fontSize: 14 }}
        data-testid="smtp-check-btn"
      >
        <RefreshCw
          size={15}
          className={state.loading ? "animate-spin" : ""}
        />{" "}
        {state.loading ? t.smtpChecking : t.smtpCheck}
      </button>
      {r && (
        <span
          className="inline-flex items-center gap-2 text-[12px] font-bold px-3 py-1.5 rounded-full"
          style={
            r.ok
              ? { background: "var(--bv-green-50)", color: "var(--bv-green)" }
              : { background: "#fef2f2", color: "#b91c1c" }
          }
          data-testid="smtp-check-result"
        >
          {r.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
          {r.ok
            ? `${t.smtpOk} · ${r.latency_ms} ms`
            : `${t.smtpFail}: ${r.error || "?"}`}
        </span>
      )}
      {r && !r.ok && r.hint && (
        <span
          className="text-[12px] block basis-full mt-1"
          style={{ color: "#b45309" }}
        >
          💡 {r.hint}
        </span>
      )}
    </div>
  );
};

const MessageCard = ({ m, t, lang, onDelete }) => (
  <div className="card" style={{ padding: 22 }} data-testid={`admin-message-${m.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div>
        <div className="font-bold text-[16px]" style={{ color: "var(--bv-heading)" }}>
          {m.name}
        </div>
        <a
          href={`mailto:${m.email}`}
          className="inline-flex items-center gap-1.5 text-[13px] hover:underline"
          style={{ color: "var(--bv-green)" }}
        >
          <Mail size={13} /> {m.email}
        </a>
      </div>
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-1 rounded-full"
          style={
            m.email_sent
              ? { background: "var(--bv-green-50)", color: "var(--bv-green)" }
              : { background: "#fef2f2", color: "#b91c1c" }
          }
          data-testid={`admin-email-status-${m.id}`}
        >
          {m.email_sent ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
          {m.email_sent ? t.emailSent : t.emailFailed}
        </span>
        <button
          onClick={() => onDelete(m.id)}
          className="w-8 h-8 rounded-lg grid place-items-center transition-colors hover:bg-red-50"
          style={{ color: "#b91c1c" }}
          aria-label="Delete"
          data-testid={`admin-delete-${m.id}`}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 mb-3">
      <span
        className="text-[12px] font-semibold px-3 py-1 rounded-full"
        style={{ background: "var(--bv-green-100)", color: "var(--bv-green)" }}
      >
        {m.inquiry_type}
      </span>
      {m.restaurant && (
        <span
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1 rounded-full"
          style={{ background: "#f1f5f9", color: "#475569" }}
        >
          <Store size={12} /> {m.restaurant}
        </span>
      )}
      {m.whatsapp && (
        <a
          href={`https://wa.me/${m.whatsapp.replace(/\D/g, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1 rounded-full hover:underline"
          style={{ background: "#e7f8ec", color: "#128c4b" }}
          data-testid={`admin-whatsapp-${m.id}`}
        >
          <Phone size={12} /> {m.whatsapp}
        </a>
      )}
    </div>
    <p
      className="text-[14px] leading-relaxed whitespace-pre-wrap mb-3"
      style={{ color: "var(--bv-ink)" }}
    >
      {m.message}
    </p>
    {!m.email_sent && m.email_error && (
      <div
        className="text-[12px] px-3 py-2 rounded-lg mb-3 font-mono break-words"
        style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" }}
        data-testid={`admin-email-error-${m.id}`}
      >
        {m.email_error}
      </div>
    )}
    <div className="text-[12px]" style={{ color: "var(--bv-muted)" }}>
      {new Date(m.created_at).toLocaleString(lang === "es" ? "es-CR" : "en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })}
      {m.locale ? ` · ${m.locale.toUpperCase()}` : ""}
    </div>
  </div>
);

const PAGE_SIZE = 50;

const SOURCE_LABELS = {
  direct: "Direct / Bookmark",
  google: "Google (organic)",
  bing: "Bing",
  duckduckgo: "DuckDuckGo",
  yahoo: "Yahoo",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  twitter: "Twitter / X",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  linktree: "Linktree",
  whatsapp: "WhatsApp",
  reddit: "Reddit",
  other: "Other",
};

const MetricCard = ({ icon: Icon, label, value, tint = "green" }) => {
  const tints = {
    green: { bg: "var(--bv-green-50)", fg: "var(--bv-green)" },
    ink: { bg: "#eef2f7", fg: "var(--bv-heading)" },
    warm: { bg: "#fff4e5", fg: "#b45309" },
    rose: { bg: "#fdecec", fg: "#b91c1c" },
  };
  const c = tints[tint] || tints.green;
  return (
    <div className="card" style={{ padding: 18 }} data-testid={`metric-${label}`}>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-xl grid place-items-center"
          style={{ background: c.bg, color: c.fg }}
        >
          <Icon size={18} />
        </div>
        <div
          className="text-[12px] font-bold uppercase tracking-wider"
          style={{ color: "var(--bv-muted)" }}
        >
          {label}
        </div>
      </div>
      <div
        className="font-display font-num"
        style={{
          fontSize: 32,
          fontWeight: 800,
          color: "var(--bv-heading)",
          lineHeight: 1,
          letterSpacing: "-0.02em",
        }}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
};

const RangeTabs = ({ t, value, onChange }) => {
  const opts = [
    { key: "1d", label: t.range1d },
    { key: "7d", label: t.range7d },
    { key: "30d", label: t.range30d },
    { key: "all", label: t.rangeAll },
  ];
  return (
    <div
      className="inline-flex p-1 rounded-full"
      style={{ background: "#fff", border: "1px solid #e5e7eb" }}
      data-testid="stats-range-tabs"
    >
      {opts.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            onClick={() => onChange(o.key)}
            className="px-4 py-1.5 text-[13px] font-bold rounded-full transition-colors"
            style={{
              background: active ? "var(--bv-green)" : "transparent",
              color: active ? "#fff" : "var(--bv-ink)",
            }}
            data-testid={`stats-range-${o.key}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

const DailyChart = ({ daily, t }) => {
  const max = Math.max(1, ...daily.map((d) => d.count));
  return (
    <div className="card" style={{ padding: 22 }} data-testid="daily-chart">
      <div
        className="flex items-center gap-2 mb-5 text-[13px] font-bold uppercase tracking-wider"
        style={{ color: "var(--bv-muted)" }}
      >
        <BarChart3 size={15} /> {t.dailyTitle}
      </div>
      <div
        className="flex items-end gap-1.5"
        style={{ height: 140 }}
      >
        {daily.map((d) => {
          const h = Math.round((d.count / max) * 100);
          return (
            <div
              key={d.day}
              className="flex-1 flex flex-col items-center justify-end min-w-0"
              title={`${d.day}: ${d.count}`}
              data-testid={`daily-bar-${d.day}`}
            >
              <div
                className="w-full rounded-t-md transition-all"
                style={{
                  height: `${Math.max(h, d.count > 0 ? 4 : 2)}%`,
                  background:
                    d.count > 0 ? "var(--bv-green)" : "rgba(11,107,58,0.12)",
                }}
              />
            </div>
          );
        })}
      </div>
      <div
        className="flex justify-between mt-2 text-[10px] tabular-nums"
        style={{ color: "var(--bv-muted)" }}
      >
        <span>{daily[0]?.day.slice(5) || ""}</span>
        <span>{daily[Math.floor(daily.length / 2)]?.day.slice(5) || ""}</span>
        <span>{daily[daily.length - 1]?.day.slice(5) || ""}</span>
      </div>
    </div>
  );
};

const SourcesTable = ({ sources, t }) => (
  <div className="card" style={{ padding: 22 }} data-testid="sources-table">
    <div
      className="flex items-center gap-2 mb-4 text-[13px] font-bold uppercase tracking-wider"
      style={{ color: "var(--bv-muted)" }}
    >
      <ExternalLink size={15} /> {t.sourcesTitle}
    </div>
    {sources.length === 0 ? (
      <div className="py-6 text-center text-[14px]" style={{ color: "var(--bv-muted)" }}>
        {t.noData}
      </div>
    ) : (
      <table className="w-full text-[14px]">
        <thead>
          <tr
            className="text-left text-[11px] font-bold uppercase tracking-wider"
            style={{ color: "var(--bv-muted)" }}
          >
            <th className="pb-2 font-bold">{t.sourceCol}</th>
            <th className="pb-2 text-right font-bold">{t.countCol}</th>
            <th className="pb-2 text-right font-bold w-24">{t.percentCol}</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => (
            <tr
              key={s.source}
              className="border-t"
              style={{ borderColor: "#f1f5f9" }}
              data-testid={`source-row-${s.source}`}
            >
              <td className="py-2.5 font-semibold" style={{ color: "var(--bv-heading)" }}>
                {SOURCE_LABELS[s.source] || s.source}
              </td>
              <td className="py-2.5 text-right tabular-nums" style={{ color: "var(--bv-ink)" }}>
                {s.count.toLocaleString()}
              </td>
              <td className="py-2.5 text-right">
                <div className="flex items-center justify-end gap-2">
                  <span
                    className="inline-block h-1.5 rounded-full"
                    style={{
                      width: `${Math.max(8, Math.min(80, s.percent))}px`,
                      background: "var(--bv-green)",
                    }}
                  />
                  <span
                    className="tabular-nums text-[12px] font-semibold"
                    style={{ color: "var(--bv-muted)" }}
                  >
                    {s.percent}%
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const StatsPanel = ({ t }) => {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authedRequest(() => api.get(`/admin/stats?range=${range}`))
      .then(({ data: d }) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  const totals = data?.totals || {};
  const metrics = [
    { key: "page_view", icon: Eye, label: t.metricViews, tint: "green" },
    { key: "android_click", icon: Play, label: t.metricAndroid, tint: "green" },
    { key: "ios_click", icon: Apple, label: t.metricIos, tint: "ink" },
    { key: "contact_submit", icon: Send, label: t.metricContact, tint: "warm" },
    { key: "hero_cta", icon: MousePointerClick, label: t.metricHero, tint: "green" },
    { key: "audience_user_cta", icon: MousePointerClick, label: t.metricAudienceUser, tint: "green" },
    { key: "audience_partner_cta", icon: Store, label: t.metricAudiencePartner, tint: "ink" },
    { key: "partner_cta", icon: Store, label: t.metricPartner, tint: "ink" },
    { key: "community_cta", icon: Heart, label: t.metricCommunity, tint: "rose" },
    { key: "instagram_click", icon: Instagram, label: t.metricInstagram, tint: "warm" },
    { key: "lang_switch", icon: MessageSquare, label: t.metricLang, tint: "ink" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-stats-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2
            className="font-display text-[24px] mb-1"
            style={{ color: "var(--bv-heading)", fontWeight: 800 }}
          >
            {t.statsTitle}
          </h2>
          <div className="text-[13px]" style={{ color: "var(--bv-muted)" }}>
            {t.statsSubtitle}
          </div>
        </div>
        <RangeTabs t={t} value={range} onChange={setRange} />
      </div>

      {loading && !data ? (
        <div className="text-center py-10" style={{ color: "var(--bv-muted)" }}>
          {t.loading}
        </div>
      ) : !data ? (
        <div className="card text-center py-10" style={{ color: "var(--bv-muted)" }}>
          {t.noData}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {metrics.map((m) => (
              <MetricCard
                key={m.key}
                icon={m.icon}
                label={m.label}
                value={totals[m.key] || 0}
                tint={m.tint}
              />
            ))}
          </div>

          <FunnelPanel totals={totals} t={t} />

          <div className="grid lg:grid-cols-2 gap-6">
            <DailyChart daily={data.daily || []} t={t} />
            <SourcesTable sources={data.sources || []} t={t} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <HourlyChart hourly={data.hourly || []} t={t} />
            <WeekdayChart weekday={data.weekday || []} t={t} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <BreakdownTable
              title={t.localeTitle}
              icon={Globe}
              testid="locales-breakdown"
              rows={(data.locales || []).map((l) => ({
                key: l.locale,
                count: l.count,
                percent: l.percent,
              }))}
              labelMap={{ es: t.langEs, en: t.langEn }}
            />
            <BreakdownTable
              title={t.inquiriesTitle}
              icon={MessageSquare}
              testid="inquiries-breakdown"
              rows={(data.inquiries || []).map((i) => ({
                key: i.type,
                count: i.count,
                percent: i.percent,
              }))}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <BreakdownTable
              title={t.countriesTitle}
              icon={Globe}
              testid="countries-breakdown"
              rows={(data.countries || []).map((c) => ({
                key: c.country || "—",
                count: c.count,
                percent: c.percent,
                flag: codeToFlag(c.country_code),
              }))}
            />
            <BreakdownTable
              title={t.regionsTitle}
              icon={MapPin}
              testid="regions-breakdown"
              rows={(data.regions || []).map((r) => ({
                key: r.region || "—",
                count: r.count,
                percent: r.percent,
                secondary: r.country || undefined,
                flag: codeToFlag(r.country_code),
              }))}
            />
          </div>
        </>
      )}
    </div>
  );
};

const HourlyChart = ({ hourly, t }) => {
  const max = Math.max(1, ...hourly.map((d) => d.count));
  return (
    <div className="card" style={{ padding: 22 }} data-testid="hourly-chart">
      <div
        className="flex items-center gap-2 mb-5 text-[13px] font-bold uppercase tracking-wider"
        style={{ color: "var(--bv-muted)" }}
      >
        <Clock size={15} /> {t.hourlyTitle}
      </div>
      <div className="flex items-end gap-[3px]" style={{ height: 110 }}>
        {hourly.map((d) => {
          const h = Math.round((d.count / max) * 100);
          return (
            <div
              key={d.hour}
              className="flex-1 flex flex-col items-center justify-end min-w-0"
              title={`${String(d.hour).padStart(2, "0")}:00 — ${d.count}`}
              data-testid={`hourly-bar-${d.hour}`}
            >
              <div
                className="w-full rounded-t transition-all"
                style={{
                  height: `${Math.max(h, d.count > 0 ? 6 : 2)}%`,
                  background:
                    d.count > 0 ? "var(--bv-green)" : "rgba(11,107,58,0.12)",
                }}
              />
            </div>
          );
        })}
      </div>
      <div
        className="flex justify-between mt-2 text-[10px] tabular-nums"
        style={{ color: "var(--bv-muted)" }}
      >
        <span>00h</span>
        <span>06h</span>
        <span>12h</span>
        <span>18h</span>
        <span>23h</span>
      </div>
    </div>
  );
};

const WeekdayChart = ({ weekday, t }) => {
  const max = Math.max(1, ...weekday.map((d) => d.count));
  return (
    <div className="card" style={{ padding: 22 }} data-testid="weekday-chart">
      <div
        className="flex items-center gap-2 mb-5 text-[13px] font-bold uppercase tracking-wider"
        style={{ color: "var(--bv-muted)" }}
      >
        <Calendar size={15} /> {t.weekdayTitle}
      </div>
      <div className="flex items-end gap-2" style={{ height: 130 }}>
        {weekday.map((d) => {
          const h = Math.round((d.count / max) * 100);
          return (
            <div
              key={d.weekday}
              className="flex-1 flex flex-col items-center min-w-0"
            >
              <div
                className="w-full flex items-end justify-center"
                style={{ height: 100 }}
              >
                <div
                  className="w-full rounded-t transition-all"
                  style={{
                    height: `${Math.max(h, d.count > 0 ? 8 : 2)}%`,
                    background:
                      d.count > 0
                        ? "var(--bv-green)"
                        : "rgba(11,107,58,0.12)",
                  }}
                  title={`${t.weekdays[d.weekday]}: ${d.count}`}
                  data-testid={`weekday-bar-${d.weekday}`}
                />
              </div>
              <div
                className="text-[10px] mt-2 font-bold uppercase tracking-wider"
                style={{ color: "var(--bv-muted)" }}
              >
                {t.weekdays[d.weekday]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BreakdownTable = ({ title, icon: Icon, rows, labelMap, testid }) => (
  <div className="card" style={{ padding: 22 }} data-testid={testid}>
    <div
      className="flex items-center gap-2 mb-4 text-[13px] font-bold uppercase tracking-wider"
      style={{ color: "var(--bv-muted)" }}
    >
      <Icon size={15} /> {title}
    </div>
    {rows.length === 0 ? (
      <div className="py-6 text-center text-[13px]" style={{ color: "var(--bv-muted)" }}>
        —
      </div>
    ) : (
      <ul className="space-y-3">
        {rows.map((r) => (
          <li key={r.key}>
            <div className="flex items-baseline justify-between text-[13px] mb-1">
              <span className="font-semibold flex items-baseline gap-2" style={{ color: "var(--bv-heading)" }}>
                {r.flag && (
                  <span aria-hidden style={{ fontSize: 15 }}>
                    {r.flag}
                  </span>
                )}
                {labelMap?.[r.key] || r.key}
                {r.secondary && (
                  <span
                    className="text-[11px] font-normal"
                    style={{ color: "var(--bv-muted)" }}
                  >
                    {r.secondary}
                  </span>
                )}
              </span>
              <span
                className="tabular-nums font-num font-bold"
                style={{ color: "var(--bv-ink)" }}
              >
                {r.count.toLocaleString()}
                <span
                  className="ml-2 text-[11px] font-semibold"
                  style={{ color: "var(--bv-muted)" }}
                >
                  {r.percent}%
                </span>
              </span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ background: "rgba(11,107,58,0.08)" }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(3, Math.min(100, r.percent))}%`,
                  background: "var(--bv-green)",
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const codeToFlag = (cc) => {
  if (!cc || cc.length !== 2) return "";
  const base = 0x1f1e6;
  const a = cc.toUpperCase().charCodeAt(0) - 65;
  const b = cc.toUpperCase().charCodeAt(1) - 65;
  if (a < 0 || a > 25 || b < 0 || b > 25) return "";
  return String.fromCodePoint(base + a) + String.fromCodePoint(base + b);
};

const FunnelStage = ({ label, value, prev, tint }) => {
  const percent = prev > 0 ? (value / prev) * 100 : 0;
  const width = prev > 0 ? Math.max(percent, value > 0 ? 8 : 3) : 100;
  return (
    <div className="mb-3" data-testid={`funnel-stage-${label}`}>
      <div className="flex items-baseline justify-between text-[13px] mb-1.5">
        <span className="font-semibold" style={{ color: "var(--bv-heading)" }}>
          {label}
        </span>
        <span
          className="tabular-nums font-num font-bold"
          style={{ color: "var(--bv-ink)" }}
        >
          {value.toLocaleString()}
          {prev > 0 && prev !== value && (
            <span
              className="ml-2 text-[11px] font-semibold"
              style={{ color: "var(--bv-muted)" }}
            >
              {percent.toFixed(0)}%
            </span>
          )}
        </span>
      </div>
      <div
        className="h-2.5 rounded-full overflow-hidden"
        style={{ background: "rgba(11,107,58,0.08)" }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${width}%`,
            background: tint || "var(--bv-green)",
          }}
        />
      </div>
    </div>
  );
};

const FunnelCard = ({ title, stages, t }) => {
  const first = stages[0]?.value || 0;
  const last = stages[stages.length - 1]?.value || 0;
  const overall = first > 0 ? ((last / first) * 100).toFixed(1) : "0";
  return (
    <div className="card" style={{ padding: 22 }} data-testid={`funnel-card-${title}`}>
      <div
        className="text-[13px] font-bold uppercase tracking-wider mb-4"
        style={{ color: "var(--bv-muted)" }}
      >
        {title}
      </div>
      {stages.map((s, i) => (
        <FunnelStage
          key={s.label}
          label={s.label}
          value={s.value}
          prev={i === 0 ? s.value : stages[i - 1].value}
          tint={s.tint}
        />
      ))}
      {first > 0 && (
        <div
          className="mt-3 pt-3 text-[12px] flex items-center justify-between"
          style={{ borderTop: "1px dashed #e5e7eb", color: "var(--bv-muted)" }}
        >
          <span>
            {last.toLocaleString()} {t.funnelOf} {first.toLocaleString()}
          </span>
          <span
            className="font-bold tabular-nums"
            style={{ color: "var(--bv-green)" }}
          >
            {overall}% {t.funnelConversion}
          </span>
        </div>
      )}
    </div>
  );
};

const FunnelPanel = ({ totals, t }) => {
  const views = totals.page_view || 0;
  const appInterest = (totals.android_click || 0) + (totals.ios_click || 0);
  const partnerInterest =
    (totals.partner_cta || 0) + (totals.audience_partner_cta || 0);
  const submits = totals.contact_submit || 0;

  return (
    <div className="space-y-2" data-testid="funnel-panel">
      <div>
        <h3
          className="font-display text-[18px] mb-1"
          style={{ color: "var(--bv-heading)", fontWeight: 800 }}
        >
          {t.funnelTitle}
        </h3>
        <div className="text-[13px]" style={{ color: "var(--bv-muted)" }}>
          {t.funnelSubtitle}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6 pt-3">
        <FunnelCard
          title={t.funnelUsers}
          t={t}
          stages={[
            { label: t.funnelStep1, value: views },
            { label: t.funnelStep2App, value: appInterest },
          ]}
        />
        <FunnelCard
          title={t.funnelPartners}
          t={t}
          stages={[
            { label: t.funnelStep1, value: views },
            {
              label: t.funnelStep2Partner,
              value: partnerInterest,
              tint: "var(--bv-heading)",
            },
            { label: t.funnelStep3, value: submits, tint: "#b45309" },
          ]}
        />
      </div>
    </div>
  );
};

const AdminDashboard = ({ t, lang }) => {
  const [tab, setTab] = useState("stats");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const { data } = await authedRequest(() =>
        api.get(`/admin/messages?skip=${skip}&limit=${PAGE_SIZE}`)
      );
      setTotal(data.total);
      setItems((prev) => (skip === 0 ? data.items : [...prev, ...data.items]));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "messages") load(0);
  }, [load, tab]);

  const onDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await authedRequest(() => api.delete(`/admin/messages/${id}`));
    setItems((prev) => prev.filter((m) => m.id !== id));
    setTotal((prev) => prev - 1);
  };

  return (
    <main className="max-w-[1100px] mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div
        className="inline-flex p-1 rounded-full mb-8"
        style={{ background: "#fff", border: "1px solid #e5e7eb" }}
        data-testid="admin-tabs"
      >
        {[
          { key: "stats", label: t.tabStats, icon: BarChart3 },
          { key: "messages", label: t.tabMessages, icon: Inbox },
        ].map((o) => {
          const active = tab === o.key;
          const Icon = o.icon;
          return (
            <button
              key={o.key}
              onClick={() => setTab(o.key)}
              className="flex items-center gap-2 px-5 py-2 text-[13px] font-bold rounded-full transition-colors"
              style={{
                background: active ? "var(--bv-green)" : "transparent",
                color: active ? "#fff" : "var(--bv-ink)",
              }}
              data-testid={`admin-tab-${o.key}`}
            >
              <Icon size={14} /> {o.label}
            </button>
          );
        })}
      </div>

      {tab === "stats" ? (
        <StatsPanel t={t} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <h1
                className="font-display text-[28px]"
                style={{ color: "var(--bv-heading)", fontWeight: 800 }}
              >
                {t.subtitle}
              </h1>
              <div className="text-[14px]" style={{ color: "var(--bv-muted)" }} data-testid="admin-total-count">
                {total} {t.messages}
              </div>
            </div>
            <button
              onClick={() => load(0)}
              className="btn-ghost"
              style={{ padding: "10px 20px", fontSize: 14 }}
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={15} /> {t.refresh}
            </button>
          </div>

          <div className="mb-6">
            <SmtpCheckButton t={t} />
          </div>

          {loading && items.length === 0 ? (
            <div className="text-center py-20" style={{ color: "var(--bv-muted)" }}>
              {t.loading}
            </div>
          ) : items.length === 0 ? (
            <div className="card text-center py-16" data-testid="admin-empty-state">
              <Inbox size={36} className="mx-auto mb-4" color="var(--bv-muted)" />
              <p style={{ color: "var(--bv-muted)" }}>{t.empty}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((m) => (
                <MessageCard key={m.id} m={m} t={t} lang={lang} onDelete={onDelete} />
              ))}
            </div>
          )}

          {items.length < total && (
            <div className="text-center mt-8">
              <button
                onClick={() => load(items.length)}
                className="btn-primary"
                disabled={loading}
                data-testid="admin-load-more-btn"
              >
                {t.loadMore}
              </button>
            </div>
          )}
        </>
      )}

      <div className="text-center mt-10">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-[13px] font-bold"
          style={{ color: "var(--bv-green)" }}
          data-testid="admin-back-to-site-link"
        >
          <ExternalLink size={14} /> {t.backToSite}
        </a>
      </div>
    </main>
  );
};

export default function AdminPage() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("bv_lang") || "es";
    } catch {
      return "es";
    }
  });
  const [user, setUser] = useState(null); // null = checking, false = logged out

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authedRequest(() => api.get("/auth/me"));
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const t = T[lang];

  const onLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout failed:", error);
    }
    setUser(false);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bv-sand)" }} data-testid="admin-root">
      <AdminHeader t={t} lang={lang} setLang={setLang} onLogout={user ? onLogout : null} />
      {user === null ? (
        <div className="text-center py-24" style={{ color: "var(--bv-muted)" }}>
          {t.loading}
        </div>
      ) : user === false ? (
        <AdminLogin t={t} onSuccess={setUser} />
      ) : (
        <AdminDashboard t={t} lang={lang} />
      )}
    </div>
  );
}
